import { useEffect, useRef, useState, useCallback } from 'react';
import { SIGN_OUT_REASON_KEY, recordIdleTimeout } from '../services/careAuthService';

/** Idle sign-out after 90 minutes, warning at 85 (docs/PLAN_SECURITY_HARDENING_2026_10.md). */
export const IDLE_LIMIT_MS = 90 * 60 * 1000;
export const IDLE_WARN_MS = 85 * 60 * 1000;
/** Absolute session cap, mirrored server-side via auth_time (12 h). */
export const MAX_SESSION_MS = 12 * 60 * 60 * 1000;

const ACTIVITY_KEY = 't1care:lastActivity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown'];
const WRITE_THROTTLE_MS = 15 * 1000;
const TICK_MS = 20 * 1000;

function readLastActivity() {
  try {
    const v = Number(window.localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeLastActivity(ts) {
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

export function setSignOutReason(reason) {
  try {
    window.sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
  } catch {
    /* ignore */
  }
}

export function consumeSignOutReason() {
  try {
    const v = window.sessionStorage.getItem(SIGN_OUT_REASON_KEY);
    if (v) window.sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
    return v || null;
  } catch {
    return null;
  }
}

/**
 * Tracks user activity across tabs (localStorage) and signs the user out when
 * idle for IDLE_LIMIT_MS or when the session is older than MAX_SESSION_MS.
 *
 * @param {object} p
 * @param {boolean} p.active         true when a care user is signed in
 * @param {number|null} p.authTimeS  token auth_time (seconds) for the 12-hour cap
 * @param {() => Promise<void>} p.signOut
 * @returns {{ idleWarning: boolean, idleRemainingMs: number, staySignedIn: () => void }}
 */
export function useSessionGuard({ active, authTimeS, signOut }) {
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleRemainingMs, setIdleRemainingMs] = useState(IDLE_LIMIT_MS);
  const lastWriteRef = useRef(0);
  const signingOutRef = useRef(false);

  const bump = useCallback(() => {
    const now = Date.now();
    if (now - lastWriteRef.current < WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    writeLastActivity(now);
    setIdleWarning(false);
  }, []);

  const staySignedIn = useCallback(() => {
    lastWriteRef.current = 0;
    bump();
  }, [bump]);

  useEffect(() => {
    if (!active || typeof window === 'undefined') {
      setIdleWarning(false);
      signingOutRef.current = false;
      return undefined;
    }
    // Fresh session: reset the shared activity clock.
    lastWriteRef.current = 0;
    writeLastActivity(Date.now());

    const onActivity = () => bump();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const endSession = async (reason, idleMs) => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      setSignOutReason(reason);
      if (reason === 'idle') await recordIdleTimeout(Math.round(idleMs / 60000));
      try {
        await signOut();
      } catch (e) {
        console.warn('Sign-out after timeout failed:', e?.message || e);
      }
    };

    const tick = () => {
      const now = Date.now();
      if (authTimeS && now - authTimeS * 1000 > MAX_SESSION_MS) {
        endSession('session-expired', 0);
        return;
      }
      const last = readLastActivity() || now;
      const idle = now - last;
      setIdleRemainingMs(Math.max(0, IDLE_LIMIT_MS - idle));
      if (idle >= IDLE_LIMIT_MS) {
        endSession('idle', idle);
      } else if (idle >= IDLE_WARN_MS) {
        setIdleWarning(true);
      } else {
        setIdleWarning(false);
      }
    };
    tick();
    const interval = window.setInterval(tick, TICK_MS);
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) tick();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, [active, authTimeS, signOut, bump]);

  return { idleWarning, idleRemainingMs, staySignedIn };
}
